import CarePlanSortType from '@features/carePlans/components/CarePlanSortType'
import { colors, sizes } from '@lmc/common/constants'
import type { CarePlan, Carer, Resident } from '@lmc/ts-common'
import {
  Button,
  Chevron,
  EmptyState,
  Icon,
  NoDataIcon,
  NotStartedIcon,
  PageHeader
} from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { ProTag } from 'components/ProTag'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import _, { find } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../actions'
import { postJSON } from '../../../common/services/api'
import { getNominative } from '../../../common/utils'
import { CarePlanNextReviewDate } from '../../../components/carePlans'
import CarePlansPopup from '../../../components/carePlans/CarePlansPopup'
import {
  CarePlansPaywall,
  HelpLink,
  ListItem,
  Spinner
} from '../../../components/generic'
import PdfExport from '../../../components/pdf/PdfExport'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { history } from '../../../store'

//TODO: define proper AppState type
type AppState = {
  global: {
    home: {
      preferredCarePlansNominative: string
      preferredPeopleNominative: string
      features?: {
        carePlans?: { enabled: boolean }
        pro?: { enabled: boolean }
      }
    }
  }
  data: {
    carers: Array<Carer>
    residents: Array<Resident>
  }
  carePlans: { carePlanSortType: string | null }
}

type Adl = {
  carePlan?: CarePlan
  icon: string
  color: string
  name: string
  id: string
  alert?: {
    type?: string
    text?: string
  }
}

const isDesktop = isBrowser || isTablet

const RefactoredResidentCarePlansDashboardScreen = () => {
  const { t } = useTranslation([NAMESPACE.CARE_PLANS])
  const [isClicked, setIsClicked] = useState(false)
  const dispatch = useDispatch()
  const { id } = useParams()

  const carers = useSelector((state: AppState) => state.data.carers)
  const adls = useSelector((state: AppState) => Selectors.sortAdls(state))
  const features = useSelector((state: AppState) => state.global.home)
  const residents = useSelector((state: AppState) => state.data.residents)
  const sortType = useSelector(
    (state: AppState) => state.carePlans.carePlanSortType
  )
  const carePlanNominative = useSelector(
    (state: AppState) => state.global.home.preferredCarePlansNominative
  )
  const preferredPeopleNominative = useSelector(
    (state: AppState) => state.global.home.preferredPeopleNominative
  )

  const hasAccessToCreateCarePlan = useAbilityAccess([
    {
      i: 'create',
      this: 'CarePlan'
    }
  ])
  const hasAccessToViewCarePlan = useAbilityAccess([
    {
      i: 'view',
      this: 'CarePlan'
    }
  ])

  const handleClick = () => {
    setIsClicked((prev) => !prev)
  }

  const getHasCarePlansEnabled = useCallback(() => {
    return (
      features &&
      features.features?.carePlans?.enabled &&
      features.features?.pro?.enabled
    )
  }, [features])

  const redirectToCarePlan = (carePlan: CarePlan) => {
    history.push(`/residents/${id}/care-plans/${carePlan.id}`)
  }

  const createCarePlan = ({
    carePlanAdl,
    resident
  }: {
    carePlanAdl: string
    resident: string
    carePlanType?: string
  }) => {
    postJSON({
      url: 'list/care-plans',
      method: 'POST',
      json: { carePlanAdl, resident }
    })
      .then((res) => {
        redirectToCarePlan(res.result)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const fetchCarePlan = useCallback(
    (residentId: string) => {
      if (getHasCarePlansEnabled()) {
        dispatch(Operations.fetchResidentAdls(residentId))
      }
    },
    [dispatch, getHasCarePlansEnabled]
  )

  const setSortType = useCallback(
    (type: string | null) => {
      dispatch(Actions.setCarePlanSortType(type))
    },
    [dispatch]
  )

  const openNewCarePlanModal = ({ resident }: { resident: string }) => {
    dispatch(
      Actions.openModal({
        modalId: 'addCarePlan',
        data: { resident }
      })
    )
    dispatch(Actions.addForm({ formId: 'addCarePlan' }))
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (getHasCarePlansEnabled() && id) {
      fetchCarePlan(id)
      setSortType('a-z')
    }
  }, [id, getHasCarePlansEnabled, fetchCarePlan, setSortType])

  const getSubLabel = (
    carePlan: CarePlan | undefined,
    carers: Array<Carer>
  ) => {
    if (!carePlan || !carePlan.editedOn)
      return t(carePlansConstants.NO_REVIEWS_YET)

    const carer = carePlan.editedBy && find(carers, { id: carePlan.editedBy })
    const date = dayjs(carePlan.editedOn).format('DD-MM-YYYY • HH:mm')

    const prefix = carePlan.nextReviewDate
      ? t(carePlansConstants.REVIEWED)
      : t(carePlansConstants.CREATED)
    if (!carer || !carer.name)
      return (
        <span>
          {prefix} {date}
        </span>
      )
    return (
      <span>
        {prefix} {date} • {`${carer.name.first} ${carer.name.last}`}
      </span>
    )
  }

  const handleSelect = (adl: {
    carePlan?: CarePlan
    id: string
    name?: string
  }) => {
    if (!adl.carePlan) {
      createCarePlan({
        carePlanAdl: adl.id,
        resident: id!,
        carePlanType: adl.name
      })
    } else {
      redirectToCarePlan(adl.carePlan)
    }
  }

  const addCustomAdl = () => {
    openNewCarePlanModal({ resident: id! })
  }

  const renderHeader = (resident: Resident, title: string) => {
    const date = dayjs().format('DD-MM-YYYY')
    const pdfTitle = `${resident.name.first} ${
      resident.name.last
    }'s ${getNominative(carePlanNominative, t(carePlansConstants.CARE_PLAN))} - ${date}`
    const pdfUrl = `/api/list/residents/${resident.id}/pdf/care-plan`
    const addCarePlanDisabled =
      resident.status === 'pre-assessment' || resident.isActiveInitialAssessment

    return (
      <PageHeader
        title={title}
        sticky
        moreInfoLink={SUPPORT_LINK}
        onBack={
          !isDesktop
            ? () => history.replace?.(`/residents/${id}/profile`)
            : undefined
        }
      >
        <ProTag />
        <HeaderButtonsContainer>
          <PdfButtonWrapper>
            <PdfExport
              pdfTitle={pdfTitle}
              url={pdfUrl}
              text={
                isDesktop
                  ? t(carePlansConstants.DOWNLOAD_PDF)
                  : t(carePlansConstants.PDF)
              }
            />
          </PdfButtonWrapper>
          <div>
            <Button
              variant="primary"
              dataTestId="add-cp"
              onClick={addCustomAdl}
              disabled={addCarePlanDisabled}
              disabledByPermission={!hasAccessToCreateCarePlan}
            >
              <Icon name={'PlusOutlined'} />
              {`${t(carePlansConstants.ADD)} ${getNominative(carePlanNominative, t(carePlansConstants.CARE_PLAN))}`}
            </Button>
          </div>
        </HeaderButtonsContainer>
      </PageHeader>
    )
  }

  const renderAdl = (adl: Adl, carers: Array<Carer>, index: number) => {
    const customName = adl.carePlan && adl.carePlan.customName

    const showCarePlanRiskAssessmentsPopup =
      adl?.carePlan?.riskAssessments?.length

    return (
      <ListItem
        dataTestId="care-plans"
        icon={adl.icon}
        iconColor={adl.color}
        key={adl.id || index}
        label={customName || adl.name}
        onSelect={() => {
          if (!hasAccessToViewCarePlan) {
            return
          }
          handleSelect(adl)
        }}
        subLabel={getSubLabel(adl?.carePlan, carers)}
        multiSelect
        subLabelChildren={
          showCarePlanRiskAssessmentsPopup ? (
            <CarePlansPopup
              carePlanRiskAssessments={adl.carePlan?.riskAssessments}
            />
          ) : null
        }
      >
        <CarePlanNextReviewDate
          date={
            (adl.carePlan ? adl.carePlan.nextReviewDate : undefined) as
              | string
              | undefined
          }
          noFurtherSupportRequired={
            adl.carePlan?.noFurtherSupportRequired ?? false
          }
          alert={adl.alert}
          style={{ alignSelf: 'center' }}
        />
      </ListItem>
    )
  }

  const renderSortType = () => {
    return (
      <SortContainer>
        <SortByText>{t(carePlansConstants.SORT_BY)}</SortByText>
        <SortWrapper>
          <CarePlanSortType onChange={setSortType} sortType={sortType} />
        </SortWrapper>
      </SortContainer>
    )
  }

  const renderAdls = (adls: Array<Adl>, carers: Array<Carer>) => {
    if (!adls) return <Spinner />
    const currentAdls = _.filter(
      adls,
      (d) => !(d.carePlan && d.carePlan.noFurtherSupportRequired)
    )
    const archivedAdls = _.filter(
      adls,
      (d: Adl) => d.carePlan && d.carePlan.noFurtherSupportRequired
    )
    return (
      <AdlsContainer data-cy="cp-list">
        {isDesktop && renderSortType()}
        <AdlSectionHeader>Current</AdlSectionHeader>
        {currentAdls.map((adl, index) => renderAdl(adl, carers, index))}
        <SubTitle
          $hasArchivedItems={!!archivedAdls.length}
          onClick={handleClick}
        >
          {t(carePlansConstants.NO_FURTHER_SUPPORT_REQUIRED)}{' '}
          {!!archivedAdls.length && (
            <Chevron direction={isClicked ? 'up' : 'down'} />
          )}
        </SubTitle>
        <AnimateHeight
          duration={500}
          height={isClicked ? 'auto' : 0}
          animateOpacity
        >
          {(archivedAdls as Array<Adl>).map((adl: Adl, index: number) =>
            renderAdl(adl, carers, index)
          )}
        </AnimateHeight>
        {!archivedAdls.length && (
          <EmptyState
            icon={<NoDataIcon />}
            title={t(carePlansConstants.NOTHING_TO_SEE_HERE)}
            description={getRenderAdlsBlankState(carePlanNominative, t)}
          >
            <a
              href={NO_IDENTIFIED_NEED_ARTICLE}
              rel="noreferrer"
              target="_blank"
            >
              Find out more
            </a>
          </EmptyState>
        )}
      </AdlsContainer>
    )
  }

  const renderInitialAssessmentInfo = (resident: Resident) => (
    <div>
      <EmptyState
        icon={<NotStartedIcon />}
        title={t(carePlansConstants.FINALISE_INITIAL_ASSESSMENT, {
          name: resident.name.first,
          carePlanNominative: getNominative(
            carePlanNominative,
            t(carePlansConstants.CARE_PLAN)
          )
        })}
        description={t(
          carePlansConstants.FINALISE_INITIAL_ASSESSMENT_DESCRIPTION,
          {
            preferredPeopleNominative: getNominative(
              preferredPeopleNominative,
              t(carePlansConstants.RESIDENT)
            ),
            carePlanNominative: getNominative(
              carePlanNominative,
              t(carePlansConstants.CARE_PLAN)
            )
          }
        )}
      />
      <HelpLink supportLink={PRE_ASSESSMENT_ARTICLE} />
    </div>
  )

  const resident = find(residents, { id })

  if (!resident || !getHasCarePlansEnabled()) {
    return (
      <CarePlansPaywall
        preferredCarePlansNominative={getNominative(
          carePlanNominative,
          t(carePlansConstants.CARE_PLAN)
        )}
        preferredPeopleNominative={getNominative(
          preferredPeopleNominative,
          'service users'
        ).toLocaleLowerCase()}
      />
    )
  }
  if (!id || !resident) return <Spinner />

  const isInitialAssessment =
    resident.status === 'pre-assessment' || resident.isActiveInitialAssessment
  const title = `${resident.name.first || ''} ${
    resident.name.last || ''
  }'s ${getNominative(carePlanNominative, t(carePlansConstants.CARE_PLAN))}`

  return (
    <Wrapper>
      {renderHeader(resident, title)}
      {isInitialAssessment
        ? renderInitialAssessmentInfo(resident)
        : renderAdls(adls, carers)}
    </Wrapper>
  )
}

const getRenderAdlsBlankState = (
  carePlanNominative: string,
  t: (
    translationConst: string,
    interpolation?: Record<string, string>
  ) => string
) =>
  t(carePlansConstants.BLANK_STATE_DESCRIPTION_ADLS, {
    carePlanNominative: getNominative(
      carePlanNominative,
      t(carePlansConstants.CARE_PLAN)
    )
  })

const SUPPORT_LINK =
  'https://support.logmycare.co.uk/en/collections/3459514-care-plans'
const NO_IDENTIFIED_NEED_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3362768-how-does-no-further-support-required-work'
const PRE_ASSESSMENT_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3376601-how-do-i-finalise-a-initial-assessment'

const Wrapper = styled.div`
  padding: 0px 0px 50px;
  margin: 0 auto;
  max-width: ${sizes.childScreen}px;
`

const HeaderButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const PdfButtonWrapper = styled.div`
  padding-right: 8px;
`

const SortContainer = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
  top: 28px;
`

const SortByText = styled.span`
  color: ${colors.darkgrey};
  display: block;
  margin-right: 12px;
`

const SortWrapper = styled.div`
  position: relative;
  top: 2px;
`

const AdlsContainer = styled.div``

const AdlSectionHeader = styled.h3`
  padding-bottom: 10px;
  margin-bottom: 2px;
  padding-left: 5px;
`

const SubTitle = styled.h3<{ $hasArchivedItems: boolean }>`
  margin-top: 30px;
  margin-bottom: 2px;
  padding-left: 5px;
  padding-bottom: ${(props) => (props.$hasArchivedItems ? '0' : '10px')};
  border-bottom: 1px solid ${colors.grey};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export default RefactoredResidentCarePlansDashboardScreen
